<form [formGroup]="formGroup" [bitSubmit]="submit" bit-dialog dialogSize="large">
  <span bitDialogTitle>
    {{ title }}
    @if (!loading && editParams$ && (editParams$ | async)?.name) {
      <span class="tw-text-sm tw-normal-case tw-text-muted">{{ (editParams$ | async)?.name }}</span>
    }
    @if (isRevoked) {
      <span bitBadge variant="secondary">{{ "revoked" | i18n }}</span>
    }
  </span>
  <div bitDialogContent>
    @if (loading) {
      <i
        class="bwi bwi-spinner bwi-spin tw-text-muted"
        title="{{ 'loading' | i18n }}"
        aria-hidden="true"
      ></i>
      <span class="tw-sr-only">{{ "loading" | i18n }}</span>
    }
    @if (!loading && organization$ | async; as organization) {
      <bit-tab-group [(selectedIndex)]="tabIndex">
        <bit-tab [label]="'role' | i18n">
          @if (!editMode) {
            <p bitTypography="body1">{{ "inviteUserDesc" | i18n }}</p>
            @if (
              {
                remainingSeats: remainingSeats$ | async,
                batchLimit: emailBatchLimit$ | async,
                isDynamicSeatPlan: isDynamicSeatPlan$ | async,
              };
              as inviteInfo
            ) {
              <bit-form-field>
                <bit-label>{{ "email" | i18n }}</bit-label>
                <input id="emails" type="text" appAutoFocus bitInput formControlName="emails" />
                @if (inviteInfo.isDynamicSeatPlan) {
                  <bit-hint>{{
                    "inviteMultipleEmailsNoSeatLimit" | i18n: inviteInfo.batchLimit
                  }}</bit-hint>
                }
                @if (!inviteInfo.isDynamicSeatPlan && inviteInfo.remainingSeats > 1) {
                  <bit-hint>{{
                    "inviteMultipleEmailsWithSeatLimit"
                      | i18n: inviteInfo.batchLimit : inviteInfo.remainingSeats
                  }}</bit-hint>
                }
                @if (!inviteInfo.isDynamicSeatPlan && inviteInfo.remainingSeats === 1) {
                  <bit-hint>{{ "inviteSingleEmailDesc" | i18n }}</bit-hint>
                }
                @if (!inviteInfo.isDynamicSeatPlan && inviteInfo.remainingSeats <= 0) {
                  <bit-hint>{{ "inviteZeroEmailDesc" | i18n }}</bit-hint>
                }
              </bit-form-field>
            }
          }
          <bit-radio-group formControlName="type" [block]="true">
            <bit-label>
              {{ "memberRole" | i18n }}
              <a
                bitLink
                target="_blank"
                rel="noreferrer"
                appA11yTitle="{{ 'learnMoreAboutMemberRoles' | i18n }}"
                href="https://bitwarden.com/help/user-types-access-control/"
                slot="end"
              >
                <i class="bwi bwi-question-circle" aria-hidden="true"></i>
              </a>
            </bit-label>
            <bit-radio-button id="userTypeUser" [value]="organizationUserType.User">
              <bit-label>{{ "user" | i18n }}</bit-label>
              <bit-hint>{{ "userDesc" | i18n }}</bit-hint>
            </bit-radio-button>
            <bit-radio-button id="userTypeAdmin" [value]="organizationUserType.Admin">
              <bit-label>{{ "admin" | i18n }}</bit-label>
              <bit-hint>{{ "adminDesc" | i18n }}</bit-hint>
            </bit-radio-button>
            <bit-radio-button id="userTypeOwner" [value]="organizationUserType.Owner">
              <bit-label>{{ "owner" | i18n }}</bit-label>
              <bit-hint>{{ "ownerDesc" | i18n }}</bit-hint>
            </bit-radio-button>
            <bit-radio-button
              id="userTypeCustom"
              [value]="organizationUserType.Custom"
              [disabled]="!organization.useCustomPermissions || null"
            >
              <bit-label>{{ "custom" | i18n }}</bit-label>
              <bit-hint>
                @if (!organization.useCustomPermissions) {
                  <p>
                    {{ "customDescNonEnterpriseStart" | i18n
                    }}<a
                      bitLink
                      href="https://bitwarden.com/contact/"
                      target="_blank"
                      rel="noreferrer"
                      >{{ "customDescNonEnterpriseLink" | i18n }}</a
                    >{{ "customDescNonEnterpriseEnd" | i18n }}
                  </p>
                } @else {
                  <p>{{ "customDesc" | i18n }}</p>
                }
              </bit-hint>
            </bit-radio-button>
          </bit-radio-group>
          @if (customUserTypeSelected) {
            <div class="tw-grid tw-grid-cols-12 tw-gap-4" [formGroup]="permissionsGroup">
              <div class="tw-col-span-4">
                <bit-form-control>
                  <input type="checkbox" bitCheckbox formControlName="accessEventLogs" />
                  <bit-label>{{ "accessEventLogs" | i18n }}</bit-label>
                </bit-form-control>
                <bit-form-control>
                  <input type="checkbox" bitCheckbox formControlName="accessImportExport" />
                  <bit-label>{{ "accessImportExport" | i18n }}</bit-label>
                </bit-form-control>
                <bit-form-control>
                  <input type="checkbox" bitCheckbox formControlName="accessReports" />
                  <bit-label>{{ "accessReports" | i18n }}</bit-label>
                </bit-form-control>
              </div>
              <div class="tw-col-span-4">
                <app-nested-checkbox
                  parentId="manageAllCollections"
                  [checkboxes]="permissionsGroup.controls.manageAllCollectionsGroup"
                >
                </app-nested-checkbox>
              </div>
              <div class="tw-col-span-4">
                <div class="tw-mb-3">
                  <bit-form-control>
                    <input type="checkbox" bitCheckbox formControlName="manageGroups" />
                    <bit-label>{{ "manageGroups" | i18n }}</bit-label>
                  </bit-form-control>
                  <bit-form-control>
                    <input type="checkbox" bitCheckbox formControlName="manageSso" />
                    <bit-label>{{ "manageSso" | i18n }}</bit-label>
                  </bit-form-control>
                  <bit-form-control>
                    <input type="checkbox" bitCheckbox formControlName="managePolicies" />
                    <bit-label>{{ "managePolicies" | i18n }}</bit-label>
                  </bit-form-control>
                  <bit-form-control>
                    <input
                      id="manageUsers"
                      type="checkbox"
                      bitCheckbox
                      formControlName="manageUsers"
                    />
                    <bit-label>{{ "manageUsers" | i18n }}</bit-label>
                  </bit-form-control>
                  <bit-form-control>
                    <input type="checkbox" bitCheckbox formControlName="manageResetPassword" />
                    <bit-label>{{ "manageAccountRecovery" | i18n }}</bit-label>
                  </bit-form-control>
                </div>
              </div>
            </div>
          }
          @if (organization.useSecretsManager) {
            <h3 class="tw-mt-4">
              {{ "secretsManager" | i18n }}
              <a
                bitLink
                target="_blank"
                rel="noreferrer"
                appA11yTitle="{{ 'learnMore' | i18n }}"
                href="https://bitwarden.com/help/manage-your-organization/#access-to-secrets-manager"
              >
                <i class="bwi bwi-question-circle" aria-hidden="true"></i>
              </a>
            </h3>
            <p class="tw-text-muted">{{ "secretsManagerAccessDescription" | i18n }}</p>
            <bit-form-control>
              <input
                type="checkbox"
                [disabled]="isOnSecretsManagerStandalone"
                bitCheckbox
                formControlName="accessSecretsManager"
              />
              <bit-label>
                {{ "userAccessSecretsManagerGA" | i18n }}
              </bit-label>
            </bit-form-control>
          }
          @if (isExternalIdVisible) {
            <bit-form-field>
              <bit-label>{{ "externalId" | i18n }}</bit-label>
              <input bitInput type="text" formControlName="externalId" readonly />
              <bit-hint>{{ "externalIdDesc" | i18n }}</bit-hint>
            </bit-form-field>
          }
          @if (isSsoExternalIdVisible) {
            <bit-form-field>
              <bit-label>{{ "ssoExternalId" | i18n }}</bit-label>
              <input bitInput type="text" formControlName="ssoExternalId" readonly />
              <bit-hint>{{ "ssoExternalIdDesc" | i18n }}</bit-hint>
            </bit-form-field>
          }
        </bit-tab>
        @if (organization.useGroups) {
          <bit-tab [label]="'groups' | i18n">
            <div class="tw-mb-6">
              {{
                (restrictEditingSelf$ | async)
                  ? ("restrictedGroupAccess" | i18n)
                  : ("groupAccessUserDesc" | i18n)
              }}
            </div>
            <bit-access-selector
              formControlName="groups"
              [items]="groupAccessItems"
              [columnHeader]="'groups' | i18n"
              [selectorLabelText]="'selectGroups' | i18n"
              [emptySelectionText]="'noGroupsAdded' | i18n"
              [hideMultiSelect]="restrictEditingSelf$ | async"
            ></bit-access-selector>
          </bit-tab>
        }
        <bit-tab [label]="'collections' | i18n">
          @if (restrictEditingSelf$ | async) {
            <div class="tw-mb-6">
              {{ "cannotAddYourselfToCollections" | i18n }}
            </div>
          }
          @if (
            !(restrictEditingSelf$ | async) &&
            (organization.useGroups || !(canAssignAccessToAnyCollection$ | async))
          ) {
            <div class="tw-mb-6">
              @if (organization.useGroups) {
                <span>
                  {{ "userPermissionOverrideHelperDesc" | i18n }}
                </span>
              }
              @if (!(canAssignAccessToAnyCollection$ | async)) {
                <span>
                  {{ "restrictedCollectionAssignmentDesc" | i18n }}
                </span>
              }
            </div>
          }
          <bit-access-selector
            [permissionMode]="PermissionMode.Edit"
            formControlName="access"
            [showGroupColumn]="organization.useGroups"
            [items]="collectionAccessItems"
            [columnHeader]="'collection' | i18n"
            [selectorLabelText]="'selectCollections' | i18n"
            [emptySelectionText]="'noCollectionsAdded' | i18n"
            [hideMultiSelect]="restrictEditingSelf$ | async"
          ></bit-access-selector
        ></bit-tab>
      </bit-tab-group>
    }
  </div>
  <ng-container bitDialogFooter>
    <button type="submit" bitButton bitFormButton buttonType="primary" [disabled]="loading">
      {{ "save" | i18n }}
    </button>
    <button
      type="button"
      bitButton
      bitFormButton
      buttonType="secondary"
      (click)="cancel()"
      [disabled]="loading"
    >
      {{ "cancel" | i18n }}
    </button>
    <div class="tw-flex tw-gap-2 tw-ml-auto">
      @if (editMode && isRevoked) {
        <button
          type="button"
          bitButton
          bitFormButton
          buttonType="secondary"
          [bitAction]="restore"
          [disabled]="loading"
        >
          {{ "restore" | i18n }}
        </button>
      }
      @if (editMode && !isRevoked) {
        <button
          type="button"
          bitButton
          bitFormButton
          buttonType="secondary"
          [bitAction]="revoke"
          [disabled]="loading"
        >
          {{ "revoke" | i18n }}
        </button>
      }
      @if (this.editMode && !(editParams$ | async)?.managedByOrganization) {
        <button
          type="button"
          buttonType="danger"
          bitButton
          bitFormButton
          [bitAction]="remove"
          [disabled]="loading"
        >
          {{ "remove" | i18n }}
        </button>
      }
      @if (this.editMode && (editParams$ | async)?.managedByOrganization) {
        <button
          type="button"
          buttonType="danger"
          bitButton
          bitFormButton
          [bitAction]="delete"
          [disabled]="loading"
        >
          {{ "delete" | i18n }}
        </button>
      }
    </div>
  </ng-container>
</form>
